export type CrmId =
  | "any"
  | "hubspot"
  | "salesforce"
  | "sfmc"
  | "optimove"
  | "mailchimp"
  | "marketo"
  | "salesloft";

export type HorizontalAlignment = "left" | "center" | "right";
export type VerticalSlot = "top" | "center" | "bottom";
export type FontWeight = "normal" | "bold";
export type SizingStrategy = "fixed" | "native-length" | "precomputed-bucket";

export type TextLayer = {
  template: string;
  fontFamily: string;
  fontWeight: FontWeight;
  fontSize: number;
  lineHeight: number;
  color: string;
  horizontalAlignment: HorizontalAlignment;
  verticalSlot: VerticalSlot;
  padding: number;
};

export type PlayIndicator = {
  enabled: boolean;
  size: number;
  foregroundColor: string;
  backgroundColor: string;
};

export type SizingBucket = {
  maxCharacters: number;
  fontSize: number;
  lineHeight: number;
};

export type EmailThumbnailConfig = {
  version: 1;
  backgroundUrl: string;
  destinationUrl: string;
  width: number;
  height: number;
  maxLines: number;
  fallbackColor: string;
  fallbackText: string;
  headline: TextLayer;
  supportingLine: TextLayer | null;
  playIndicator: PlayIndicator;
  crmId: CrmId;
  fieldMappings: Record<string, string>;
  sizingStrategy: SizingStrategy;
  bucketField: string | null;
  sizingBuckets: SizingBucket[];
  sampleData: Record<string, string>;
};

export type TemplateSegment =
  | { type: "text"; value: string }
  | { type: "field"; field: string };

export type TemplateDiagnosticCode =
  | "unmatched-opening-delimiter"
  | "unmatched-closing-delimiter"
  | "invalid-field-name"
  | "unmapped-field"
  | "invalid-crm-token";

export type TemplateDiagnostic = {
  code: TemplateDiagnosticCode;
  message: string;
  index?: number;
  field?: string;
};

export type CrmAdapter = {
  id: CrmId;
  label: string;
  renderField: (field: string) => string;
  isValidToken: (token: string) => boolean;
  capabilities: {
    nativeLength: boolean;
    precomputedBucket: boolean;
  };
};

export const createDefaultEmailThumbnailConfig = (): EmailThumbnailConfig => ({
  version: 1,
  backgroundUrl: "",
  destinationUrl: "",
  width: 600,
  height: 338,
  maxLines: 2,
  fallbackColor: "#111827",
  fallbackText: "Personalized video thumbnail",
  headline: {
    template: "",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontWeight: "bold",
    fontSize: 32,
    lineHeight: 38,
    color: "#FFFFFF",
    horizontalAlignment: "center",
    verticalSlot: "center",
    padding: 24,
  },
  supportingLine: null,
  playIndicator: {
    enabled: true,
    size: 48,
    foregroundColor: "#FFFFFF",
    backgroundColor: "#000000",
  },
  crmId: "any",
  fieldMappings: {},
  sizingStrategy: "fixed",
  bucketField: null,
  sizingBuckets: [
    { maxCharacters: 18, fontSize: 32, lineHeight: 38 },
    { maxCharacters: 29, fontSize: 28, lineHeight: 34 },
    { maxCharacters: 42, fontSize: 24, lineHeight: 30 },
  ],
  sampleData: {},
});
